import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { SiteContentProvider } from "@/contexts/SiteContentContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";
import RouteAnalytics from "@/components/RouteAnalytics";

import Home from "@/pages/Home";
import Camps from "@/pages/Camps";
import CampDetails from "@/pages/CampDetails";
import CampRegister from "@/pages/CampRegister";
import CampResults from "@/pages/CampResults";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import PlayerDashboard from "@/pages/PlayerDashboard";
import ClaimAccount from "@/pages/ClaimAccount";
import RecruiterClaim from "@/pages/RecruiterClaim";
import WWBAInterest from "@/pages/interest/WWBAInterest";
import DominicanInterest from "@/pages/interest/DominicanInterest";
import TbcInterest from "@/pages/interest/TbcInterest";
import Trips from "@/pages/Trips";
import CreateProfile from "@/pages/CreateProfile";
import AdminInterests from "@/pages/admin/AdminInterests";
import RecruiterSearch from "@/pages/recruiter/RecruiterSearch";
import RecruiterPlayerProfile from "@/pages/recruiter/RecruiterPlayerProfile";
import RecruiterWatchlists from "@/pages/recruiter/RecruiterWatchlists";
import RecruiterCompare from "@/pages/recruiter/RecruiterCompare";
import AdminRecruiters from "@/pages/admin/AdminRecruiters";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Prospects from "@/pages/Prospects";
import CBRNationAlumni from "@/pages/CBRNationAlumni";
import Movers from "@/pages/Movers";
import LiveTeam from "@/pages/live/LiveTeam";
import LiveGame from "@/pages/live/LiveGame";
import TeamRankings from "@/pages/live/TeamRankings";
import HowRankingsWork from "@/pages/live/HowRankingsWork";
import LiveHome from "@/pages/live/LiveHome";
import LiveSchedule from "@/pages/live/LiveSchedule";
import LiveMessages from "@/pages/live/LiveMessages";
import LiveAccount from "@/pages/live/LiveAccount";
import TeamPortfolio from "@/pages/live/TeamPortfolio";
import CompareTeams from "@/pages/live/CompareTeams";
import PlayerProfile from "@/pages/PlayerProfile";
import PublicGame from "@/pages/PublicGame";
import Portfolio from "@/pages/Portfolio";
import Leaderboards from "@/pages/Leaderboards";
import Rankings from "@/pages/Rankings";
import Awards from "@/pages/Awards";
import CampAwards from "@/pages/CampAwards";
import Commitments from "@/pages/Commitments";
import MarketplaceHome from "@/pages/marketplace/MarketplaceHome";
import MarketplaceProducts from "@/pages/marketplace/MarketplaceProducts";
import MarketplaceCollection from "@/pages/marketplace/MarketplaceCollection";
import MarketplaceProduct from "@/pages/marketplace/MarketplaceProduct";
import News from "@/pages/News";
import Article from "@/pages/Article";
import About from "@/pages/About";
import CBRLive from "@/pages/CBRLive";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminCamps from "@/pages/admin/AdminCamps";
import AdminPlayers from "@/pages/admin/AdminPlayers";
import AdminSchools from "@/pages/admin/AdminSchools";
import AdminUnclaimedPlayers from "@/pages/admin/AdminUnclaimedPlayers";
import AdminHealth from "@/pages/admin/AdminHealth";
import AdminPlayerDetail from "@/pages/admin/AdminPlayerDetail";
import AdminPitchCounts from "@/pages/admin/AdminPitchCounts";
import AdminFeedback from "@/pages/admin/AdminFeedback";
import FeedbackWidget from "@/components/FeedbackWidget";
import AdminCheckIn from "@/pages/admin/AdminCheckIn";
import CampLiveDashboard from "@/pages/admin/CampLiveDashboard";
import CampBulkEntry from "@/pages/admin/CampBulkEntry";
import CampStation from "@/pages/admin/CampStation";
import CampSelection from "@/pages/admin/CampSelection";
import CampCredentials from "@/pages/admin/CampCredentials";
import CampRecognition from "@/pages/admin/CampRecognition";
import AdminCMS from "@/pages/admin/AdminCMS";
import AdminHomepage from "@/pages/admin/AdminHomepage";
import AdminSponsors from "@/pages/admin/AdminSponsors";
import AdminMediaWall from "@/pages/admin/AdminMediaWall";
import AdminSiteContent from "@/pages/admin/AdminSiteContent";
import MediaWall from "@/pages/MediaWall";
import Sponsors from "@/pages/Sponsors";
import Sponsorship from "@/pages/Sponsorship";
import SponsorshipSuccess from "@/pages/SponsorshipSuccess";
import SponsorLogo from "@/pages/SponsorLogo";
import AdminSponsorship from "@/pages/admin/AdminSponsorship";
import ArticleEditor from "@/pages/admin/ArticleEditor";
import AdminRegistrations from "@/pages/admin/AdminRegistrations";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminEmailLog from "@/pages/admin/AdminEmailLog";
import AdminRankings from "@/pages/admin/AdminRankings";
import AdminInGameRankings from "@/pages/admin/AdminInGameRankings";
import AdminApprovalQueue from "@/pages/admin/AdminApprovalQueue";
import AdminGameReview from "@/pages/admin/AdminGameReview";
import AdminCorrections from "@/pages/admin/AdminCorrections";
import AdminLeagues from "@/pages/admin/AdminLeagues";
import AdminManualEntry from "@/pages/admin/AdminManualEntry";
import AdminImport from "@/pages/admin/AdminImport";
import AdminImportHistory from "@/pages/admin/AdminImportHistory";
import TournamentPage from "@/pages/live/TournamentPage";
import AdminSocial from "@/pages/admin/AdminSocial";
import AdminTeams from "@/pages/admin/AdminTeams";
import AdminLiveGames from "@/pages/admin/AdminLiveGames";
import AdminGamesMasterList from "@/pages/admin/AdminGamesMasterList";
import AdminGameStats from "@/pages/admin/AdminGameStats";
import AdminPaymentTest from "@/pages/admin/AdminPaymentTest";
import AdminMarketplace from "@/pages/admin/AdminMarketplace";
import AdminWwba from "@/pages/admin/AdminWwba";
import WwbaRegister from "@/pages/WwbaRegister";
import WwbaBalance from "@/pages/WwbaBalance";
import AdminClaims from "@/pages/admin/AdminClaims";
import AdminOrganizations from "@/pages/admin/AdminOrganizations";
import AdminOrganizationDetail from "@/pages/admin/AdminOrganizationDetail";
import AdminLiveTeams from "@/pages/admin/AdminLiveTeams";
import ClaimOwnership from "@/pages/live/ClaimOwnership";
import ClaimInviteAccept from "@/pages/live/ClaimInviteAccept";
import RoleInviteAccept from "@/pages/live/RoleInviteAccept";
import OrgStaff from "@/pages/live/OrgStaff";
import OrgProfile from "@/pages/live/OrgProfile";
import Membership from "@/pages/Membership";
import CommandCenter from "@/pages/CommandCenter";
import OrganizationsDirectory from "@/pages/OrganizationsDirectory";
import TeamsDirectory from "@/pages/TeamsDirectory";
import Trending from "@/pages/Trending";
import AdminTeamRoster from "@/pages/admin/AdminTeamRoster";
import AdminTournaments from "@/pages/admin/AdminTournaments";
import AdminTournamentDetail from "@/pages/admin/AdminTournamentDetail";
import InvitationResponse from "@/pages/InvitationResponse";
import QRLanding from "@/pages/QRLanding";
import EvaluatorDashboard from "@/pages/EvaluatorDashboard";
import EvaluatorCampDashboard from "@/pages/EvaluatorCampDashboard";
import EvaluatorPlayerView from "@/pages/EvaluatorPlayerView";
import QuickEval from "@/pages/QuickEval";
import { CartProvider } from "@/contexts/CartContext";
import CartDrawer from "@/components/marketplace/CartDrawer";
import CartPage from "@/pages/marketplace/CartPage";
import CheckoutPage from "@/pages/marketplace/CheckoutPage";
import OrderConfirmation from "@/pages/marketplace/OrderConfirmation";

function App() {
  return (
    <AuthProvider>
      <SiteContentProvider>
      <BrowserRouter>
        <CartProvider>
        <RouteAnalytics />
        <CartDrawer />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/camps" element={<Camps />} />
          <Route path="/marketplace" element={<MarketplaceHome />} />
          <Route path="/marketplace/products" element={<MarketplaceProducts />} />
          <Route path="/marketplace/cart" element={<CartPage />} />
          <Route path="/marketplace/checkout" element={<CheckoutPage />} />
          <Route path="/marketplace/order/:orderNumber" element={<OrderConfirmation />} />
          <Route path="/marketplace/collection/:slug" element={<MarketplaceCollection />} />
          <Route path="/marketplace/product/:slug" element={<MarketplaceProduct />} />
          <Route path="/camps/:slug" element={<CampDetails />} />
          <Route path="/camps/:slug/results" element={<CampResults />} />
          <Route path="/camps/:slug/awards" element={<CampAwards />} />
          <Route path="/camps/:slug/register" element={<CampRegister />} />
          <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/about" element={<About />} />
          <Route path="/cbr-live" element={<CBRLive />} />
          <Route path="/prospects" element={<Prospects />} />
          <Route path="/trips" element={<Trips />} />
          <Route path="/interest/dominican-republic" element={<DominicanInterest />} />
          <Route path="/interest/tbc-experience" element={<TbcInterest />} />
          <Route path="/interest/:slug" element={<WWBAInterest />} />
          <Route path="/trips/register" element={<WWBAInterest multi />} />
          <Route path="/register/:slug" element={<WwbaRegister />} />
          <Route path="/balance/:slug" element={<WwbaBalance />} />
          <Route path="/create-profile" element={<CreateProfile />} />
          <Route path="/cbr-nation-alumni" element={<CBRNationAlumni />} />
          <Route path="/movers" element={<Movers />} />
          <Route path="/trending" element={<Trending />} />
          <Route path="/organizations" element={<OrganizationsDirectory />} />
          <Route path="/teams" element={<TeamsDirectory />} />
          <Route path="/live" element={<LiveHome />} />
          <Route path="/live/claim" element={<ClaimOwnership />} />
          <Route path="/claim-org/:token" element={<ClaimInviteAccept />} />
          <Route path="/team-invite/:token" element={<RoleInviteAccept />} />
          <Route path="/live/orgs/:slug" element={<OrgProfile />} />
          <Route path="/live/orgs/:slug/staff" element={<OrgStaff />} />
          <Route path="/membership" element={<Membership />} />          <Route path="/command-center" element={<ProtectedRoute><CommandCenter /></ProtectedRoute>} />
          <Route path="/live/schedule" element={<LiveSchedule />} />
          <Route path="/live/messages" element={<LiveMessages />} />
          <Route path="/live/account" element={<LiveAccount />} />
          <Route path="/live/compare" element={<CompareTeams />} />
          <Route path="/live/teams/:slug/portfolio" element={<TeamPortfolio />} />
          <Route path="/live/teams/:slug" element={<LiveTeam />} />
          <Route path="/live/games/:id" element={<LiveGame />} />
          <Route path="/live/team-rankings" element={<TeamRankings />} />
          <Route path="/live/team-rankings/how-it-works" element={<HowRankingsWork />} />
          <Route path="/prospects/:slug" element={<PlayerProfile />} />
          <Route path="/games/:id" element={<PublicGame />} />
          <Route path="/portfolio/:slug" element={<Portfolio />} />
          <Route path="/leaderboards" element={<Leaderboards />} />
          <Route path="/leaderboards/statistics" element={<Leaderboards />} />
          <Route path="/rankings" element={<Rankings />} />
          <Route path="/awards" element={<Awards />} />
          <Route path="/commitments" element={<Commitments />} />
          <Route path="/news" element={<News />} />
          <Route path="/news/:slug" element={<Article />} />
          <Route path="/media-wall" element={<MediaWall />} />
          <Route path="/sponsors" element={<Sponsors />} />
          <Route path="/sponsorship" element={<Sponsorship />} />
          <Route path="/sponsorship/success" element={<SponsorshipSuccess />} />
          <Route path="/sponsorship/logo/:token" element={<SponsorLogo />} />
          <Route path="/invitation/:token" element={<InvitationResponse />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/my-profile" element={<ProtectedRoute><PlayerDashboard /></ProtectedRoute>} />
          <Route path="/claim/:token" element={<ClaimAccount />} />
          <Route path="/recruiter-claim/:token" element={<RecruiterClaim />} />
          <Route path="/recruiter" element={<ProtectedRoute requireRecruiter><RecruiterSearch /></ProtectedRoute>} />
          <Route path="/recruiter/players/:id" element={<ProtectedRoute requireRecruiter><RecruiterPlayerProfile /></ProtectedRoute>} />
          <Route path="/recruiter/watchlists" element={<ProtectedRoute requireRecruiter><RecruiterWatchlists /></ProtectedRoute>} />
          <Route path="/recruiter/compare" element={<ProtectedRoute requireRecruiter><RecruiterCompare /></ProtectedRoute>} />
          <Route path="/admin/recruiters" element={<ProtectedRoute requireAdmin><AdminRecruiters /></ProtectedRoute>} />
          <Route path="/admin/analytics" element={<ProtectedRoute requireAdmin><AdminAnalytics /></ProtectedRoute>} />
          <Route path="/qr/:token" element={<QRLanding />} />
          <Route path="/evaluator" element={<ProtectedRoute requireEvaluator><EvaluatorDashboard /></ProtectedRoute>} />
          <Route path="/evaluator/dashboard" element={<ProtectedRoute requireEvaluator><EvaluatorCampDashboard /></ProtectedRoute>} />
          <Route path="/evaluate/:playerId" element={<ProtectedRoute requireEvaluator><EvaluatorPlayerView /></ProtectedRoute>} />
          <Route path="/quick-eval" element={<ProtectedRoute requireEvaluator><QuickEval /></ProtectedRoute>} />

          <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/camps" element={<ProtectedRoute requireAdmin><AdminCamps /></ProtectedRoute>} />
          <Route path="/admin/camps/:campId/checkin" element={<ProtectedRoute requireAdmin><AdminCheckIn /></ProtectedRoute>} />
          <Route path="/admin/camps/:campId/live" element={<ProtectedRoute requireAdmin><CampLiveDashboard /></ProtectedRoute>} />
          <Route path="/admin/camps/:campId/bulk-entry" element={<ProtectedRoute requireAdmin><CampBulkEntry /></ProtectedRoute>} />
          <Route path="/admin/camps/:campId/station" element={<ProtectedRoute requireAdmin><CampStation /></ProtectedRoute>} />
          <Route path="/admin/camps/:campId/selection" element={<ProtectedRoute requireAdmin><CampSelection /></ProtectedRoute>} />
          <Route path="/admin/camps/:campId/credentials" element={<ProtectedRoute requireAdmin><CampCredentials /></ProtectedRoute>} />
          <Route path="/admin/camps/:campId/recognition" element={<ProtectedRoute requireAdmin><CampRecognition /></ProtectedRoute>} />
          <Route path="/admin/cms" element={<ProtectedRoute requireAdmin><AdminCMS /></ProtectedRoute>} />
          <Route path="/admin/live/leagues" element={<ProtectedRoute requireAdmin><AdminLeagues /></ProtectedRoute>} />
          <Route path="/admin/live/organizations" element={<ProtectedRoute requireAdmin><AdminOrganizations /></ProtectedRoute>} />
          <Route path="/admin/live/organizations/:id" element={<ProtectedRoute requireAdmin><AdminOrganizationDetail /></ProtectedRoute>} />
          <Route path="/admin/live/teams" element={<ProtectedRoute requireAdmin><AdminLiveTeams /></ProtectedRoute>} />
          <Route path="/admin/live/games" element={<ProtectedRoute requireAdmin><AdminLiveGames /></ProtectedRoute>} />
          <Route path="/admin/games/approval" element={<ProtectedRoute requireAdmin><AdminApprovalQueue /></ProtectedRoute>} />
          <Route path="/admin/games/approval/:id" element={<ProtectedRoute requireAdmin><AdminGameReview /></ProtectedRoute>} />
          <Route path="/admin/games/corrections" element={<ProtectedRoute requireAdmin><AdminCorrections /></ProtectedRoute>} />
          <Route path="/admin/games" element={<ProtectedRoute requireAdmin><AdminGamesMasterList /></ProtectedRoute>} />
          <Route path="/admin/game-stats" element={<ProtectedRoute requireAdmin><AdminGameStats /></ProtectedRoute>} />
          <Route path="/admin/payment-test" element={<ProtectedRoute requireAdmin><AdminPaymentTest /></ProtectedRoute>} />
          <Route path="/admin/marketplace" element={<ProtectedRoute requireAdmin><AdminMarketplace /></ProtectedRoute>} />
          <Route path="/admin/live/pitch-counts" element={<ProtectedRoute requireAdmin><AdminPitchCounts /></ProtectedRoute>} />
          <Route path="/admin/live/claims" element={<ProtectedRoute requireAdmin><AdminClaims /></ProtectedRoute>} />
          <Route path="/admin/live/manual-entry" element={<ProtectedRoute requireAdmin><AdminManualEntry /></ProtectedRoute>} />
          <Route path="/admin/live/import" element={<ProtectedRoute requireAdmin><AdminImport /></ProtectedRoute>} />
          <Route path="/admin/live/import-history" element={<ProtectedRoute requireAdmin><AdminImportHistory /></ProtectedRoute>} />
          <Route path="/live/tournaments/:id" element={<TournamentPage />} />
          <Route path="/admin/homepage" element={<ProtectedRoute requireAdmin><AdminHomepage /></ProtectedRoute>} />
          <Route path="/admin/sponsors" element={<ProtectedRoute requireAdmin><AdminSponsors /></ProtectedRoute>} />
          <Route path="/admin/sponsorship" element={<ProtectedRoute requireAdmin><AdminSponsorship /></ProtectedRoute>} />
          <Route path="/admin/media-wall" element={<ProtectedRoute requireAdmin><AdminMediaWall /></ProtectedRoute>} />
          <Route path="/admin/site-content" element={<ProtectedRoute requireAdmin><AdminSiteContent /></ProtectedRoute>} />
          <Route path="/admin/cms/articles/:articleId" element={<ProtectedRoute requireAdmin><ArticleEditor /></ProtectedRoute>} />
          <Route path="/admin/players" element={<ProtectedRoute requireAdmin><AdminPlayers /></ProtectedRoute>} />
          <Route path="/admin/schools" element={<ProtectedRoute requireAdmin><AdminSchools /></ProtectedRoute>} />
          <Route path="/admin/unclaimed-players" element={<ProtectedRoute requireAdmin><AdminUnclaimedPlayers /></ProtectedRoute>} />
          <Route path="/admin/interests" element={<ProtectedRoute requireAdmin><AdminInterests /></ProtectedRoute>} />
          <Route path="/admin/wwba" element={<ProtectedRoute requireAdmin><AdminWwba /></ProtectedRoute>} />
          <Route path="/admin/health" element={<ProtectedRoute requireAdmin><AdminHealth /></ProtectedRoute>} />
          <Route path="/admin/players/:id" element={<ProtectedRoute requireAdmin><AdminPlayerDetail /></ProtectedRoute>} />
          <Route path="/admin/registrations" element={<ProtectedRoute requireAdmin><AdminRegistrations /></ProtectedRoute>} />
          <Route path="/admin/payments" element={<ProtectedRoute requireAdmin><AdminPayments /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute requireAdmin><AdminUsers /></ProtectedRoute>} />
          <Route path="/admin/email-log" element={<ProtectedRoute requireAdmin><AdminEmailLog /></ProtectedRoute>} />
          <Route path="/admin/rankings" element={<ProtectedRoute requireAdmin><AdminRankings /></ProtectedRoute>} />
          <Route path="/admin/ingame-rankings" element={<ProtectedRoute requireAdmin><AdminInGameRankings /></ProtectedRoute>} />
          <Route path="/admin/social" element={<ProtectedRoute requireAdmin><AdminSocial /></ProtectedRoute>} />
          <Route path="/admin/teams" element={<ProtectedRoute requireAdmin><AdminTeams /></ProtectedRoute>} />
          <Route path="/admin/teams/:teamId" element={<ProtectedRoute requireAdmin><AdminTeamRoster /></ProtectedRoute>} />
          <Route path="/admin/tournaments" element={<ProtectedRoute requireAdmin><AdminTournaments /></ProtectedRoute>} />
          <Route path="/admin/tournaments/:id" element={<ProtectedRoute requireAdmin><AdminTournamentDetail /></ProtectedRoute>} />
          <Route path="/admin/feedback" element={<ProtectedRoute requireAdmin><AdminFeedback /></ProtectedRoute>} />
        </Routes>
        <FeedbackWidget />
        <Toaster richColors position="top-right" />
        </CartProvider>
      </BrowserRouter>
      </SiteContentProvider>
    </AuthProvider>
  );
}

export default App;
